import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "../../../common/DataTable";
import SearchBox from "../../../common/SearchBox";
import Pagination from "../../../common/Pagination";
import StatusBadge from "../../../common/StatusBadge";
import BASE_URL from "../../../../config/config";
import "../../../../css/PostList.css";

// تابع debounce
const debounce = (func, delay) => {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => func.apply(this, args), delay);
  };
};

export default function PostList() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({
    status: "",
    category: "",
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const navigate = useNavigate();
  // در کامپوننت PostList
  const [sortConfig, setSortConfig] = useState({
    key: "created_at",
    direction: "desc",
  });
  const [csrfToken, setCsrfToken] = useState("");

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const res = await fetch(`${BASE_URL}/csrf-token`, {
          credentials: "include",
        });
        const data = await res.json();
        setCsrfToken(data.csrfToken);
      } catch {
        setError("دریافت توکن امنیتی با خطا");
      }
    };
    fetchToken();
  }, []);

  const debounce = (func, delay) => {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => func.apply(this, args), delay);
    };
  };

  // حالت‌های جستجو
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const searchInputRef = useRef(null);

  // تابع debounce شده برای جستجو
  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchTerm(value);
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
    }, 500),
    []
  );

  const handleSearchChange = (value) => {
    setSearchInput(value);
    debouncedSearch(value);
  };

  // تابع جدید برای دریافت دسته‌بندی‌ها
  const fetchCategories = async () => {
    try {
      setCategoriesLoading(true);
      const response = await fetch(`${BASE_URL}/blog/categories`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) throw new Error("خطا در دریافت دسته‌بندی‌ها");

      const data = await response.json();

      // اینجا را با توجه به ساختار واقعی پاسخ API تنظیم کنید
      const receivedCategories = Array.isArray(data)
        ? data
        : Array.isArray(data.data)
        ? data.data
        : Array.isArray(data.categories)
        ? data.categories
        : [];

      setCategories(receivedCategories);
    } catch (err) {
      console.error("Error fetching categories:", err);
      setCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        ...(searchTerm && { search: searchTerm }),
        ...(filters.status && { status: filters.status }),
        ...(filters.category && { category: filters.category }),
      }).toString();

      const url = `${BASE_URL}/blog/posts?${queryParams}`;
      // console.log('Request URL:', url); // اضافه کردن این خط برای دیباگ

      const response = await fetch(`${BASE_URL}/blog/posts?${queryParams}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) throw new Error("خطا در دریافت پست‌ها");

      const data = await response.json();
      //  console.log('API Response:', data);
      setPosts(data.data);
      setPagination((prev) => ({
        ...prev,
        totalPages: data.pagination.totalPages,
        totalItems: data.pagination.totalItems,
      }));
    } catch (err) {
      console.error("Error fetching posts:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [pagination.currentPage, searchTerm, filters]);

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const handleStatusFilter = (e) => {
    setFilters((prev) => ({ ...prev, status: e.target.value }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleCategoryFilter = (e) => {
    setFilters((prev) => ({ ...prev, category: e.target.value }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleEdit = (postId) => {
    navigate(`/admin/blog/posts/edit/${postId}`);
  };

  const handleDelete = async (postId) => {
    if (!window.confirm("آیا از حذف این پست مطمئن هستید؟")) return;

    try {
      const response = await fetch(`${BASE_URL}/blog/posts/${postId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "CSRF-Token": csrfToken,
        },
        credentials: "include",
      });

      if (!response.ok) throw new Error("خطا در حذف پست");

      // اگر پست حذف شده در صفحه فعلی بود و آخرین پست صفحه بود، به صفحه قبل برو
      if (posts.length === 1 && pagination.currentPage > 1) {
        setPagination((prev) => ({
          ...prev,
          currentPage: prev.currentPage - 1,
        }));
      } else {
        fetchPosts();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSort = (newSortConfig) => {
    setSortConfig(newSortConfig);
    // می‌توانید اینجا درخواست جدید به سرور بزنید یا داده‌ها را محلی مرتب کنید
    const sortedData = [...posts].sort((a, b) => {
      if (a[newSortConfig.key] < b[newSortConfig.key]) {
        return newSortConfig.direction === "asc" ? -1 : 1;
      }
      if (a[newSortConfig.key] > b[newSortConfig.key]) {
        return newSortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
    setPosts(sortedData);
  };

  const columns = [
    {
      key: "title",
      label: "عنوان",
      render: (value, row) => (
        <div className="post-title-cell">
          {row.cover_image && (
            <img
              src={`/uploads/blog/posts/${row.cover_image}`}
              alt={row.title}
              className="post-thumbnail"
            />
          )}
          <span>{value}</span>
        </div>
      ),
    },
    {
      key: "status",
      label: "وضعیت",
      render: (value) => <StatusBadge status={value} />,
    },
    { key: "category_name", label: "دسته‌بندی" },
    {
      key: "tags",
      label: "تگ‌ها",
      render: (value, row) => (
        <div className="tags-container">
          {Array.isArray(row.tags) && row.tags.length > 0 ? (
            row.tags.map((tag) => (
              <span key={tag.name} className="tag">
                {tag.name}
              </span>
            ))
          ) : (
            <span className="tag-empty">بدون تگ</span>
          )}
        </div>
      ),
    },
    {
      key: "created_at",
      label: "تاریخ ایجاد",
      render: (value) => new Date(value).toLocaleDateString("fa-IR"),
    },
  ];

  if (loading || categoriesLoading)
    return <div className="loading">در حال بارگذاری...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="post-list-container">
      <div className="post-list-header">
        <h2>مدیریت پست‌ها</h2>
        <button
          className="btn-primary"
          onClick={() => navigate("/admin/blog/posts/add")}
        >
          افزودن پست جدید
        </button>
      </div>

      <div className="filters-container">
        <div className="search-box">
          <input
            type="text"
            ref={searchInputRef}
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="جستجو در عنوان..."
            className="search-input"
          />
          {searchTerm && (
            <button
              className="clear-search-btn"
              onClick={() => {
                setSearchInput("");
                setSearchTerm("");
              }}
            >
              پاک کردن
            </button>
          )}
        </div>

        <div className="select-filters">
          <select
            value={filters.status}
            onChange={handleStatusFilter}
            className="filter-select"
          >
            <option value="">همه وضعیت‌ها</option>
            <option value="draft">پیش‌نویس</option>
            <option value="published">منتشر شده</option>
            <option value="archived">آرشیو</option>
          </select>

          <select
            value={filters.category}
            onChange={handleCategoryFilter}
            className="filter-select"
            disabled={categoriesLoading}
          >
            <option value="">همه دسته‌بندی‌ها</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={posts}
        sortConfig={sortConfig}
        onSort={handleSort}
        renderActions={(post) => (
          <div className="actions">
            <button className="btn-edit" onClick={() => handleEdit(post.id)}>
              ویرایش
            </button>
            <button
              className="btn-delete"
              onClick={() => handleDelete(post.id)}
            >
              حذف
            </button>
          </div>
        )}
      />

      <Pagination
        currentPage={pagination.currentPage}
        totalPages={pagination.totalPages}
        onPageChange={handlePageChange}
      />
    </div>
  );
}
